import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { classNames } from '@core/utils/functions/class-names';
import { Link as EventCustomFieldLink } from '@/types/EventsCustomFields';
import Link from 'next/link';
import MainMenuButton from '@/components/pages/event/layout/components/MainMenuButton';
import Menu from './Menu';

type EventMainMenuProps = {
    routerTab: string;
    className?: string;
    slug: string;
    title: string;
    accentColor: string;
    button1?: EventCustomFieldLink;
    button2?: EventCustomFieldLink;
    onSelect?: (selected: string) => void;
    hideTabs?: string[];
    dynamicTabs: typeof Menu;
};

const EventMainMenu = (props: EventMainMenuProps) => {
    const { routerTab, className, title, button1, button2, slug, accentColor, onSelect, hideTabs, dynamicTabs } = props;
    const [selected, setSelected] = useState(routerTab);

    const menuRef = useRef<HTMLDivElement>(null);
    const scrollIntoMenu = () => menuRef?.current && menuRef.current.scrollIntoView();
    useEffect(() => {
        if (routerTab === 'home') {
            scrollIntoMenu();
        }
    }, [routerTab]);

    // Highlight selected menu item
    const selectedMenu = (selectedItem: string) => {
        setSelected(selectedItem);
        if (onSelect) {
            onSelect(selectedItem);
        }
    };

    return (
        <div
            ref={menuRef}
            className={classNames(
                'sticky bg-white z-[100] top-0 left-0 right-0 self-start bg-white border-none',
                className
            )}
        >
            <div className="p-5 md:p-10 lg:pl-10 xl:pl-20 border-b">
                <div className="flex flex-wrap justify-between items-center flex-grow-0 flex-shrink-0 gap-12">
                    <div className="md:flex flex-auto justify-between items-start gap-5">
                        <div className="flex flex-auto flex-col justify-between items-start relative gap-4 md:gap-6">
                            <p className="font-bold text-2xl md:text-3xl text-left text-[#000a39]">{title}</p>

                            <div className="relative w-full h-8">
                                <div className="absolute left-0 right-6 md:w-full overflow-y-auto no-scrollbar flex justify-start items-start gap-6 md:gap-10 whitespace-nowrap">
                                    {[...Menu, ...dynamicTabs]
                                        .filter(m => hideTabs?.findIndex(hide => hide === m.slug) === -1)
                                        .map(item => (
                                            <div
                                                key={item.slug}
                                                className="flex justify-start items-start relative gap-2.5"
                                            >
                                                <Link href={`/event/${slug}/${item.slug}`}>
                                                    <a
                                                        className="text-md md:text-lg text-left capitalize hover:text-gray-600"
                                                        onClick={() => selectedMenu(item.slug)}
                                                        style={{
                                                            color: selected === item.slug ? accentColor : '',
                                                        }}
                                                    >
                                                        {item.title}
                                                    </a>
                                                </Link>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap md:flex-nowrap whitespace-nowrap justify-start items-start gap-5 pt-6 pb-4 md:pt-4">
                            {button2 && button2!.url && (
                                <MainMenuButton
                                    accentColor={accentColor}
                                    url={button2!.url}
                                    target={button2.target || '_blank'}
                                    title={button2.title || ''}
                                />
                            )}
                            {button1 && button1!.url && (
                                <MainMenuButton
                                    accentColor={accentColor}
                                    primary
                                    url={button1!.url}
                                    target={button1.target || '_blank'}
                                    title={button1.title || ''}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventMainMenu;
